import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { api } from "@/services/api";
import { decodeRecordId, encodeRecordId } from "@/utils/record-token";

/**
 * Which record a list has open, kept in the address bar.
 *
 * Held in component state it could not tell a reload from arriving fresh, so a
 * screen reopened whatever was last looked at. In the URL, arriving has no
 * parameter and shows the list, reloading keeps it, and Back closes the panel.
 *
 * Several lists share one screen — the complimentary tab has a club list and
 * two reason lists — so the parameter names the list it belongs to:
 * `?edit=club.NDIuMWM4`. A list ignores a parameter that is not its own, which
 * is what stops one panel opening in three places at once.
 */
export function useRecordParam<T extends { id: number }>(
  key: "view" | "edit",
  /** Identifies this list among the ones sharing the screen. */
  listKey: string,
  /** Where to fetch a record the list does not already hold. */
  endpoint: string,
): {
  editing: "new" | T | null;
  setEditing: (next: "new" | T | null) => void;
  /** True while the record is being read. Nothing is open yet. */
  loading: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries()) as Record<string, unknown>;
  const [fetched, setFetched] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  const raw = typeof params[key] === "string" ? (params[key] as string) : undefined;
  const mine = raw?.startsWith(`${listKey}.`) ? raw.slice(listKey.length + 1) : undefined;
  const id = mine && mine !== "new" ? decodeRecordId(mine) : null;

  // `replace` so the panel opening and closing does not stack history entries —
  // Back still closes the panel because the token was pushed by whoever opened it.
  const setEditing = (next: "new" | T | null) =>
    setSearchParams(
      (previous) => {
        const search = new URLSearchParams(previous);
        if (next === null) search.delete(key);
        else search.set(key, `${listKey}.${next === "new" ? "new" : encodeRecordId(next.id)}`);
        return search;
      },
      { replace: true },
    );

  // Always read the record, never the row the list happens to be holding.
  //
  // The list row is a projection and it is as old as the last time the list was
  // fetched — opening it showed stale values, and on a screen that projects
  // columns it showed a record with fields missing. One request when a record is
  // opened is a cheap price for the panel being about the record rather than
  // about what the list remembered of it.
  useEffect(() => {
    if (id === null) {
      setFetched(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setFetched(null);
    setLoading(true);
    void api
      .get<T>(`${endpoint}/${id}`)
      .then((response) => !cancelled && setFetched(response.data))
      .catch(() => !cancelled && setEditing(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, endpoint]);

  const editing: "new" | T | null = mine === "new" ? "new" : id === null ? null : fetched;
  return { editing, setEditing, loading: loading && mine !== "new" };
}
